import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiPhone, FiPhoneCall, FiTarget, FiCreditCard, FiShield, FiMessageCircle, 
  FiCheck, FiArrowRight, FiClock, FiUsers, FiZap, FiVideo, FiDollarSign 
} = FiIcons;

const ProductsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const scrollToSection = (sectionId) => {
    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const openCalendly = () => {
    window.open('https://calendly.com/fishinnovation/engagedent-ai-discovery-call', '_blank');
  };

  const products = [
    {
      name: "After Hours Receptionist",
      subtitle: "\"Donna\"",
      bestFor: "Any practice, especially DSOs",
      description: "Handles after-hours calls, books appointments, sends patient messages, and creates to-do lists for staff.",
      icon: FiPhone,
      color: "from-dental-blue to-ai-teal",
      features: [
        "After-hours to full 24/7 booking",
        "Triages calls and routes emergencies correctly",
        "HIPAA-compliant messaging so practices know who/what to follow up on",
        "Never miss a call again",
        "Automated appointment confirmations",
        "Real-time staff notifications"
      ]
    },
    {
      name: "Instant Connect Inbound",
      subtitle: "Speed-to-Lead Inbound",
      bestFor: "High-end general practices and specialists running campaigns",
      description: "Captures patients who respond to ads, email, or SMS and ensures fast booking.",
      icon: FiTarget,
      color: "from-energy-coral to-growth-green",
      features: [
        "AI answers procedure and campaign questions",
        "24/7 appointment booking",
        "Dedicated phone number for each marketing campaign",
        "Lead qualification and scoring",
        "Campaign performance tracking",
        "Instant response to marketing inquiries"
      ]
    },
    {
      name: "Instant Connect Outbound",
      subtitle: "Speed-to-Lead Outbound",
      bestFor: "General practices and specialists offering elective/procedure-based care",
      description: "Calls patients back in seconds when they engage with a campaign.",
      icon: FiPhoneCall,
      color: "from-ai-teal to-growth-green",
      features: [
        "1.5 second call-back after campaign response",
        "AI avatar video creator for social posts and patient education",
        "Dedicated phone number",
        "24/7 appointment booking",
        "Automated follow-up sequences",
        "Social media content creation"
      ]
    }
  ];

  const addOns = [
    {
      name: "Insurance Verification",
      description: "Looks up benefits and provides cost estimates to both patient and practice.",
      icon: FiShield,
      color: "from-dental-blue to-ai-teal",
      benefits: [
        "Real-time benefit verification",
        "Cost estimates for patients",
        "Reduces front desk workload",
        "Improves treatment acceptance"
      ]
    },
    {
      name: "Patient Financing",
      description: "Real-time financing discussions with pre-approval sent to dentist before consult. Gives transparency for patients and treatment planning clarity for dentists.",
      icon: FiCreditCard,
      color: "from-growth-green to-energy-coral",
      benefits: [
        "Real-time pre-approvals",
        "Treatment planning clarity",
        "Increased case acceptance",
        "Multiple financing options"
      ]
    },
    {
      name: "Pre-Op FAQ Bot",
      description: "Conversational AI that answers patient questions about procedures before treatment.",
      icon: FiMessageCircle,
      color: "from-energy-coral to-dental-blue",
      benefits: [
        "24/7 patient support",
        "Reduces staff interruptions",
        "Improves patient confidence",
        "Customizable Q&A database"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-clean-white to-gray-50">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-dental-blue via-ai-teal to-growth-green">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-clean-white mb-6">
              AI Solutions Designed for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-clean-white to-growth-green">
                Every Dental Practice
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-clean-white opacity-90 max-w-4xl mx-auto leading-relaxed">
              Choose the product that fits your practice best — and add the features that matter most.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product Comparison Matrix */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-gray mb-6">
              Compare Our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-dental-blue to-ai-teal">
                Core Products
              </span>
            </h2>
            <p className="text-xl text-slate-gray opacity-80 max-w-3xl mx-auto">
              Each solution is designed to solve specific practice challenges while working seamlessly together.
            </p>
          </motion.div>

          {/* Products Grid */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {products.map((product, index) => (
              <motion.div
                key={product.name}
                className="bg-clean-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 relative overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                {/* Background Gradient */}
                <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${product.color}`} />
                
                {/* Header */}
                <div className="text-center mb-8">
                  <div className={`w-20 h-20 bg-gradient-to-br ${product.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <SafeIcon icon={product.icon} className="text-3xl text-clean-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-gray mb-2">{product.name}</h3>
                  <p className="text-lg font-semibold text-dental-blue mb-2">{product.subtitle}</p>
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <p className="text-sm font-medium text-slate-gray">Best for:</p>
                    <p className="text-sm text-slate-gray opacity-80">{product.bestFor}</p>
                  </div>
                  <p className="text-slate-gray opacity-80 leading-relaxed">{product.description}</p>
                </div>

                {/* Features */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-gray mb-4">Key Features:</h4>
                  {product.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start space-x-3">
                      <SafeIcon icon={FiCheck} className="text-growth-green mt-1 flex-shrink-0" />
                      <span className="text-sm text-slate-gray opacity-80">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* HIPAA Compliance Badge */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-dental-blue to-ai-teal text-clean-white px-6 py-3 rounded-full">
              <SafeIcon icon={FiShield} className="text-xl" />
              <span className="font-semibold">All Products are HIPAA Compliant</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Add-Ons Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-clean-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-gray mb-6">
              Powerful{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-growth-green to-energy-coral">
                Add-Ons
              </span>
            </h2>
            <p className="text-xl text-slate-gray opacity-80 max-w-3xl mx-auto mb-8">
              Enhance any product with these specialized features. Mix and match based on your practice needs.
            </p>
            <div className="bg-ai-teal bg-opacity-10 rounded-lg p-4 max-w-2xl mx-auto">
              <p className="text-slate-gray font-medium">
                💡 Add-ons can be applied to any product or bundled across products.
              </p>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {addOns.map((addon, index) => (
              <motion.div
                key={addon.name}
                className="bg-clean-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${addon.color} rounded-2xl flex items-center justify-center mb-6`}>
                  <SafeIcon icon={addon.icon} className="text-2xl text-clean-white" />
                </div>
                
                <h3 className="text-xl font-bold text-slate-gray mb-4">{addon.name}</h3>
                <p className="text-slate-gray opacity-80 leading-relaxed mb-6">{addon.description}</p>
                
                <div className="space-y-2">
                  {addon.benefits.map((benefit, benefitIndex) => (
                    <div key={benefitIndex} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheck} className="text-growth-green text-sm flex-shrink-0" />
                      <span className="text-sm text-slate-gray opacity-80">{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-br from-slate-gray to-dental-blue">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-clean-white mb-6">
              Ready to See Which Solution{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-growth-green to-ai-teal">
                Fits Your Practice?
              </span>
            </h2>
            <p className="text-xl text-clean-white opacity-90 mb-12 leading-relaxed">
              Combine products and add-ons based on your needs. EngageDent AI makes it simple.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <motion.button
                onClick={() => scrollToSection('roi')}
                className="bg-gradient-to-r from-growth-green to-ai-teal text-clean-white px-8 py-4 rounded-xl font-semibold text-lg flex items-center space-x-3 hover:shadow-lg transition-all duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={FiDollarSign} className="text-xl" />
                <span>Calculate Your ROI</span>
              </motion.button>
              
              <motion.button
                onClick={openCalendly}
                className="bg-gradient-to-r from-energy-coral to-dental-blue text-clean-white px-8 py-4 rounded-xl font-semibold text-lg flex items-center space-x-3 hover:shadow-lg transition-all duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={FiVideo} className="text-xl" />
                <span>Book a Demo</span>
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;